import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Función auxiliar para obtener el token de autenticación del localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// Acciones asíncronas
export const fetchSeries = createAsyncThunk('series/fetchSeries', async () => {
  try {
    const response = await axios.get(`${API_URL}/api/v1/series`);
    console.log('Respuesta completa de la API:', response);
    console.log('Estructura de response.data:', response.data);
    
    // Verificar si response.data.data existe
    if (response.data.data) {
      console.log('Series encontradas en response.data.data:', response.data.data);
      return response.data.data;
    } 
    // Si no existe data, pero hay un array en response.data, podría ser ese el array de series
    else if (Array.isArray(response.data)) {
      console.log('Series encontradas directamente en response.data:', response.data);
      return response.data;
    }
    // En caso de otras estructuras, devolver lo que haya en data
    else {
      console.log('Estructura de respuesta inesperada, devolviendo response.data:', response.data);
      return response.data;
    }
  } catch (error) {
    console.error('Error al obtener series:', error);
    throw error;
  }
});

export const addSeries = createAsyncThunk('series/addSeries', async (seriesData) => {
  const authHeader = getAuthHeader();
  console.log('Enviando solicitud con cabeceras:', authHeader);
  const response = await axios.post(`${API_URL}/api/v1/series`, seriesData, authHeader);
  return response.data.data;
});

export const updateSeries = createAsyncThunk('series/updateSeries', async ({ id, seriesData }) => {
  const authHeader = getAuthHeader();
  const response = await axios.put(`${API_URL}/api/v1/series/${id}`, seriesData, authHeader);
  return response.data.data;
});

export const deleteSeries = createAsyncThunk('series/deleteSeries', async (id, { rejectWithValue }) => {
  try {
    const authHeader = getAuthHeader();
    await axios.delete(`${API_URL}/api/v1/series/${id}`, authHeader);
    return id;
  } catch (error) {
    // Capturar el mensaje de error del backend
    const errorMessage = error.response && error.response.data && error.response.data.message
      ? error.response.data.message
      : 'Error al eliminar la serie';
    return rejectWithValue({ message: errorMessage });
  }
});

// Slice
const seriesSlice = createSlice({
  name: 'series',
  initialState: {
    items: [],
    status: 'idle',
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Manejar fetchSeries
      .addCase(fetchSeries.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSeries.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Mapear los datos que vienen de la API al formato que espera el componente
        state.items = action.payload.map(series => ({
          id: series.id,
          name: series.name || series.title,  // Compatibilidad con ambos campos
          genres: Array.isArray(series.genres) ? series.genres.join(', ') : series.genres,
          releaseYear: series.releaseYear || series.year,
          totalSeasons: series.totalSeasons || series.seasons?.length || 0,
          // Otros campos que puedan ser necesarios
          description: series.description || series.synopsis,
          poster: series.poster || series.image,
          // Mantener otros campos originales
          ...series
        }));
      })
      .addCase(fetchSeries.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      // Manejar addSeries
      .addCase(addSeries.fulfilled, (state, action) => {
        const formattedSeries = {
          id: action.payload.id,
          name: action.payload.name || action.payload.title,
          genres: Array.isArray(action.payload.genres) ? action.payload.genres.join(', ') : action.payload.genres,
          releaseYear: action.payload.releaseYear || action.payload.year,
          totalSeasons: action.payload.totalSeasons || action.payload.seasons?.length || 0,
          description: action.payload.description || action.payload.synopsis,
          poster: action.payload.poster || action.payload.image,
          ...action.payload
        };
        state.items.push(formattedSeries);
      })
      // Manejar updateSeries
      .addCase(updateSeries.fulfilled, (state, action) => {
        const formattedSeries = {
          id: action.payload.id,
          name: action.payload.name || action.payload.title,
          genres: Array.isArray(action.payload.genres) ? action.payload.genres.join(', ') : action.payload.genres,
          releaseYear: action.payload.releaseYear || action.payload.year,
          totalSeasons: action.payload.totalSeasons || action.payload.seasons?.length || 0,
          description: action.payload.description || action.payload.synopsis,
          poster: action.payload.poster || action.payload.image,
          ...action.payload
        };
        const index = state.items.findIndex(series => series.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = formattedSeries;
        }
      })
      // Manejar deleteSeries
      .addCase(deleteSeries.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteSeries.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = state.items.filter(series => series.id !== action.payload);
      })
      .addCase(deleteSeries.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload.message;
      });
  }
});

export default seriesSlice.reducer;
